// src/context/theme.js

/**
 * The site theme, as one device-local choice.
 *
 * THREE STATES IN THE STORE, TWO ON THE DOM. The user picks "light" | "dark" | "system"; the DOM only
 * ever carries the RESOLVED answer, as `data-theme="light"` or `data-theme="dark"` on <html>. Storing
 * the choice rather than the resolution is what keeps "system" working: a laptop that flips at sunset
 * re-resolves, where a stored "dark" would freeze. It is also why nothing here ever writes "system" —
 * absence of the key IS system, and writing it would freeze today's default into every user's storage.
 *
 * WHY A KEY OF ITS OWN, NOT A PREFERENCE. `context/preferences.js` keys on
 * `blobhub:prefs:me[:org:<id>][:blob:<id>]` and merges defaults -> user -> org -> blob. Three things
 * follow, each on its own disqualifying. A value that resolves per org and per blob is not a theme, it
 * is a strobe: the page would change colour as you navigate into a blob. The scope segments are API
 * ids, not URL slugs, so the pre-paint boot script — which has only `location.pathname` — cannot know
 * which scope applies until after an API call, i.e. long after first paint. And
 * `components/profile/preferences.js` renders `Preferences.getRaw()` into an editable JSON pane, so a
 * mirrored key becomes a second, hand-editable source of truth. `context/session.js` is this repo's
 * own precedent for a purpose-built top-level key.
 *
 * A theme is a DEVICE preference — the same person plausibly wants a different answer on a phone and
 * on a laptop. Note `Preferences` is device-local too: it never reaches `context/api.js`. So it would
 * buy no extra durability here, only the costs above.
 *
 * THE ATTRIBUTE IS STAMPED BEFORE REACT EXISTS. The inline script in `public/index.html` writes
 * `data-theme` and the inline `color-scheme` during head parsing, before the stylesheet is even
 * requested, so the first paint is already correct. This module owns every write AFTER that one, and
 * the two apply the same three-value rule so they cannot disagree.
 *
 * NOTIFICATION, NOT RE-RENDER. Most of the theme lands through CSS custom properties and needs no
 * JavaScript at all. The exceptions paint from JS strings — Ace's `theme` prop, mermaid's config,
 * vis-network's `setOptions` — and those have to be told. `_listeners` is the same
 * static-Set-plus-unsubscribe shape `Preferences._listeners` (preferences.js:113-124) already uses, so
 * this adds no new idiom; `withTheme` is `withPreferences` with two props instead of one.
 */

const KEY = "blobhub:theme";
const ATTR = "data-theme";
const MEDIA = "(prefers-color-scheme: dark)";
// The token theme.css declares for the browser-chrome band. It is INVARIANT today (the band abuts the
// navbar, which never flips), but it is read rather than hardcoded so a later decision to flip it
// needs no JavaScript change.
const CHROME_VAR = "--bh-chrome-theme-color";

const LIGHT = "light";
const DARK = "dark";
const SYSTEM = "system";

// The choice as held in memory, plus the two things storage can independently fail at.
//
// Storage can be present and still throw — Safari's private mode has historically thrown on read AND
// on write, and a browser with site data blocked throws on access. Without a memory fallback the
// control is DEAD in those profiles: `set()` writes nothing, `apply()` re-reads nothing, and clicking
// Light does visibly nothing with no error anywhere.
//
// READING AND WRITING ARE TRACKED SEPARATELY, because they fail for different reasons and one does
// not imply the other. A full quota makes writes throw while leaving every existing key perfectly
// readable, so a failed write must not stop us reading — that would end cross-tab sync for the rest
// of the session over something that never touched the read path. Only a read that actually throws
// stops us reading.
let memoryChoice = null;
let storageReadable = true;
// Not a latch: it tracks whether the LAST write landed, and a later successful write clears it.
// Between a failed write and the next successful one, storage is holding something other than what
// the user asked for, and memory is the only place their real choice exists.
let lastWriteLanded = true;

// Absent, corrupt, or anything unrecognised reads as "system". The boot script in public/index.html
// applies exactly this rule; keep the two in step.
function normalize(raw) {
  return (LIGHT === raw || DARK === raw) ? raw : SYSTEM;
}

function mediaQuery() {
  // jsdom has no matchMedia at all, and iOS 11 — still in this project's resolved browserslist — has
  // matchMedia but does not know `prefers-color-scheme`: the query fails to parse and `.matches` is
  // simply false, which is the right answer there.
  try {
    return window.matchMedia ? window.matchMedia(MEDIA) : null;
  } catch (err) {
    return null;
  }
}

/**
 * Push the resolved theme onto <meta name="theme-color">, reading the value from theme.css.
 *
 * Deliberately NOT the two-tag `media="(prefers-color-scheme: ...)"` form: that follows the OS, so a
 * light-OS user who picks Dark would get light browser chrome sitting on a dark page — the one case an
 * explicit choice must win and structurally cannot.
 *
 * The read is guarded because it legitimately comes back empty: jest stubs every CSS import, so no
 * token is declared under test, and leaving the authored value in place is the right answer there.
 */
function applyChromeColor() {
  const meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    return;
  }
  const value = window.getComputedStyle(document.documentElement).getPropertyValue(CHROME_VAR).trim();
  if (value) {
    meta.setAttribute("content", value);
  }
}

/**
 * Test seam: drop the private module state so each case starts clean. Nothing in the app calls it.
 */
export function __resetThemeStateForTest() {
  memoryChoice = null;
  storageReadable = true;
  lastWriteLanded = true;
  Theme._last = { choice: null, resolved: null };
  Theme._installed = false;
}

export default class Theme {
  static KEY = KEY;
  static ATTR = ATTR;
  static LIGHT = LIGHT;
  static DARK = DARK;
  static SYSTEM = SYSTEM;

  static _listeners = new Set();

  // What was last stamped, so `apply()` can stay idempotent about the DOM while still notifying when
  // only the CHOICE moved — picking "Dark" while the system is already dark changes nothing visually
  // but must still move the tick in the menu.
  static _last = { choice: null, resolved: null };

  static _installed = false;

  /** The choice in force: "light" | "dark" | "system". */
  static choice() {
    if (storageReadable) {
      try {
        const stored = localStorage.getItem(KEY);
        // Storage is authoritative while it holds what we asked it to hold. The one window where it
        // does not is between a failed write and the next successful one: reading past memory there
        // would put the control straight back in the state the memory fallback exists to prevent —
        // the user picks Light, the write fails, and the page keeps rendering the old answer.
        if (lastWriteLanded || null === memoryChoice) {
          return normalize(stored);
        }
      } catch (err) {
        // A read throwing is a different capability failing from a write throwing, and only this one
        // may stop us reading.
        storageReadable = false;
      }
    }
    return normalize(memoryChoice);
  }

  /** Whether the OS is currently asking for dark. */
  static systemIsDark() {
    const mql = mediaQuery();
    return !!(mql && mql.matches);
  }

  /** The theme actually in force: "light" | "dark". */
  static resolved() {
    const choice = Theme.choice();
    return SYSTEM === choice ? (Theme.systemIsDark() ? DARK : LIGHT) : choice;
  }

  static isDark() {
    return DARK === Theme.resolved();
  }

  /**
   * Stamp <html> from the store. Idempotent against the DOM; notifies whenever the choice or the
   * resolution moved. Returns the resolved theme.
   */
  static apply() {
    const choice = Theme.choice();
    const resolved = SYSTEM === choice ? (Theme.systemIsDark() ? DARK : LIGHT) : choice;
    const changed = (choice !== Theme._last.choice) || (resolved !== Theme._last.resolved);

    const root = document.documentElement;
    if (root.getAttribute(ATTR) !== resolved) {
      root.setAttribute(ATTR, resolved);
    }
    // Mirrors what the boot script set. `color-scheme` is what makes the UA paint scrollbars, the
    // caret, native form controls and ::selection in the right mode, and it is what removes the white
    // flash in the window before the stylesheet arrives. theme.css declares it too, but an inline
    // style outranks any non-!important rule, so this MUST be rewritten on every change or the two
    // drift the first time anyone touches the control.
    root.style.colorScheme = resolved;

    Theme._last = { choice: choice, resolved: resolved };
    applyChromeColor();
    if (changed) {
      Theme._notify();
    }
    return resolved;
  }

  /** Record a choice and apply it. `choice` is "light" | "dark" | "system". */
  static set(choice) {
    const next = normalize(choice);
    memoryChoice = next;
    try {
      localStorage.setItem(KEY, next);
      // A write that lands hands authority back to storage — this is not a one-way latch, so a quota
      // that frees up restores ordinary cross-tab behaviour without a reload.
      lastWriteLanded = true;
    } catch (err) {
      // Quota, or storage disabled. The choice still governs this page through `memoryChoice`; it just
      // will not survive a reload. Failing silently matches writeScope() in preferences.js.
      lastWriteLanded = false;
    }
    return Theme.apply();
  }

  /**
   * Attach the live listeners. Call once, from src/index.js, before ReactDOM.createRoot.
   *
   * The attribute is already on <html> by then — public/index.html stamped it during head parsing —
   * so this is not what makes the theme correct. It adds the two things the boot script cannot do,
   * because it has already finished running, and re-stamps as a safety net if that script were ever
   * absent.
   */
  static install() {
    if (Theme._installed) {
      return Theme.apply();
    }
    Theme._installed = true;

    const mql = mediaQuery();
    if (mql) {
      // Only "system" follows the OS, and apply() is where that rule lives — it re-reads the choice, so
      // an explicit one resolves to itself and the flip is a no-op. Re-testing the choice here would be
      // a second copy of the same rule, and a mutation check confirmed nothing depends on it.
      const onSystemChange = () => Theme.apply();
      // `addEventListener` on a MediaQueryList needs Safari 14, and this project's browserslist still
      // resolves ios_saf 11.0-11.2, which has only the deprecated `addListener`. Feature-detect; do
      // not assume, because the modern-only form attaches NOTHING there, with no error.
      if (mql.addEventListener) {
        mql.addEventListener("change", onSystemChange);
      } else if (mql.addListener) {
        mql.addListener(onSystemChange);
      }
    }

    // Free cross-tab sync: `storage` fires in the OTHER tabs of this origin. `key` is null when
    // localStorage was cleared wholesale, which is also a reason to re-read.
    window.addEventListener("storage", (event) => {
      if (!event || null === event.key || KEY === event.key) {
        // A `storage` event only ever comes from ANOTHER tab, so what it reports is newer than an
        // unpersisted local choice. Drop the memory override rather than re-applying it over what the
        // other tab just wrote — but only when we can actually read, or we would discard the one copy
        // of the choice we have.
        if (storageReadable) {
          memoryChoice = null;
        }
        Theme.apply();
      }
    });

    return Theme.apply();
  }

  static subscribe(callback) {
    Theme._listeners.add(callback);
    return () => Theme._listeners.delete(callback);
  }

  static _notify() {
    for (const cb of Theme._listeners) {
      try { cb(); } catch (err) { /* listener error */ }
    }
  }
}

/**
 * HOC in the shape of withPreferences (preferences.js:63-110).
 *
 * Injects `theme` ("light" | "dark") and `themeChoice` ("light" | "dark" | "system"). Only for
 * components that paint from JS — an Ace pane, a mermaid diagram, a vis-network canvas. Anything
 * driven by CSS custom properties needs nothing at all.
 *
 *   export default withTheme(MyEditor);
 */
export function withTheme(WrappedComponent) {
  const React = require('react');

  class WithTheme extends React.Component {
    constructor(props) {
      super(props);
      this.state = { theme: Theme.resolved(), themeChoice: Theme.choice() };
    }

    _onThemeChange = () => {
      const theme = Theme.resolved();
      const themeChoice = Theme.choice();
      if (theme !== this.state.theme || themeChoice !== this.state.themeChoice) {
        this.setState({ theme: theme, themeChoice: themeChoice });
      }
    }

    componentDidMount() {
      this._unsub = Theme.subscribe(this._onThemeChange);
      // A change between construction and mount — a sibling's set() during the same commit — would
      // otherwise be missed.
      this._onThemeChange();
    }

    componentWillUnmount() {
      if (this._unsub) this._unsub();
    }

    render() {
      return React.createElement(WrappedComponent, {
        ...this.props,
        theme: this.state.theme,
        themeChoice: this.state.themeChoice
      });
    }
  }

  WithTheme.displayName =
    `WithTheme(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
  return WithTheme;
}
